import React, { useState, useEffect } from "react";
import { Button, message, Input, Select } from "antd";
import CustomSimpleTable from "@/components/UI/Tables/CustomSimpleTable";
import CustomModal from "@/components/UI/Modals/CustomModal";
import ConfirmModal from "@/components/UI/Modals/ConfirmModal";
import {
  useGetServiceFeesQuery,
  useAddServiceFeeMutation,
  useDeleteServiceFeeMutation,
  useUpdateServiceFeeMutation,
} from "@/services/api/serviceFee/serviceFeeApi";
import { useGetLocationsQuery } from "@/services/api/location/locationApi";
import {
  useAddVatFeeMutation,
  useDeleteVatFeeMutation,
  useGetVatFeesQuery,
  useUpdateVatFeeMutation,
} from "@/services/api/vatFee/vatFeeApi";
import {
  useGetDeliveryFeesQuery,
  useAddDeliveryFeeMutation,
  useDeleteDeliveryFeeMutation,
  useUpdateDeliveryFeeMutation,
} from "@/services/api/deliveryFee/deliveryFeeApi";
import { useGetServiceTypeQuery } from "@/services/api/event/serviceTypeApi";

const FeeSection = ({
  title,
  description,
  data,
  columns,
  onEdit,
  onDelete,
  onSelect,
  selectedKeys,
  loading,
  onShowAdd,
}) => (
  <div className="flex w-full h-full bg-white">
    <div className="w-80 p-6 flex flex-col justify-center">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">{title}</h2>
      <p className="text-gray-600">{description}</p>
    </div>

    <div className="flex-1 p-6">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">Fees</h3>
          <Button
            type="primary"
            onClick={onShowAdd}
            className="bg-orange-500 hover:bg-orange-600 border-orange-500 text-white"
          >
            + Add
          </Button>
        </div>
        <CustomSimpleTable
          data={data}
          columns={columns}
          onEdit={onEdit}
          onDelete={onDelete}
          onSelect={onSelect}
          selectedRowKeys={selectedKeys}
          showCheckbox
          showActions
          loading={loading}
        />
      </div>
    </div>
  </div>
);

const FeeModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  loading,
  fee,
  setFee,
  onSubmit,
  locationOptions,
  serviceTypeOptions,
  feeType,
  mode,
}) => (
  <CustomModal
    isOpen={isOpen}
    onClose={onClose}
    title={title}
    subtitle={subtitle}
    primaryActionText={loading ? "Saving..." : "Save"}
    onPrimaryAction={onSubmit}
    secondaryActionText="Cancel"
    onSecondaryAction={onClose}
    width={500}
    rightOffset={20}
    primaryActionLoading={loading}
  >
    <div className="p-6 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Location {mode === "edit" ? "*" : "* (select multiple)"}
        </label>
        <Select
          mode="multiple"
          value={fee.location_ids}
          onChange={(value) => setFee({ ...fee, location_ids: value })}
          options={locationOptions}
          placeholder="Select one or more locations"
          className="w-full"
          disabled={mode === "edit"}
          allowClear
          maxTagCount="responsive"
        />
      </div>

      {/* Service Type - Only show for Service Fees */}
      {feeType === "service" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Service Type *
          </label>
          <Select
            value={fee.service_type_id}
            onChange={(value) => setFee({ ...fee, service_type_id: value })}
            options={serviceTypeOptions}
            placeholder="Select service type"
            className="w-full"
            disabled={mode === "edit"}
          />
        </div>
      )}

      {/* Name - Only show for Delivery Fees */}
      {feeType === "delivery" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Name (Optional)
          </label>
          <Input
            value={fee.name}
            onChange={(e) => setFee({ ...fee, name: e.target.value })}
            placeholder="e.g., Standard, Express"
            maxLength={100}
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {feeType === "vat"
            ? "VAT Fee (%) *"
            : feeType === "delivery"
              ? "Delivery Fee (₦/km) *"
              : "Service Fee (%) *"}
        </label>
        {feeType === "delivery" ? (
          <Input
            value={fee.fee ? fee.fee.toLocaleString() : ""}
            onChange={(e) => {
              const rawValue = e.target.value.replace(/,/g, "");
              const value = parseFloat(rawValue);

              if (rawValue && (isNaN(value) || value < 0)) {
                message.warning("Please enter a valid positive number");
                return;
              }

              setFee({
                ...fee,
                fee: rawValue === "" ? 0 : value,
              });
            }}
            placeholder="100"
            prefix="₦"
          />
        ) : (
          <Input
            type="number"
            value={feeType === "vat" ? fee.vat_fee : fee.service_fee}
            onChange={(e) => {
              const value = parseFloat(e.target.value);

              if (
                (feeType === "vat" || feeType === "service") &&
                value &&
                (value < 1 || value > 100)
              ) {
                message.warning("Percentage must be between 1 and 100");
                return;
              }

              setFee({
                ...fee,
                ...(feeType === "vat"
                  ? { vat_fee: value || 0 }
                  : { service_fee: value || 0 }),
              });
            }}
            placeholder={feeType === "vat" ? "10" : "5"}
            min={1}
            max={100}
            step={0.01}
          />
        )}
        {feeType === "delivery" && (
          <p className="text-xs text-gray-500 mt-1">
            Fee charged per kilometer
          </p>
        )}
      </div>
    </div>
  </CustomModal>
);

const Fees = () => {
  // API Queries
  const {
    data: serviceFeesData,
    isLoading: isLoadingService,
    error: serviceError,
    refetch: refetchService,
  } = useGetServiceFeesQuery();
  const {
    data: vatFeesData,
    isLoading: isLoadingVat,
    error: vatError,
    refetch: refetchVat,
  } = useGetVatFeesQuery();
  const {
    data: deliveryFeesData,
    isLoading: isLoadingDelivery,
    error: deliveryError,
    refetch: refetchDelivery,
  } = useGetDeliveryFeesQuery();
  const { data: locationsData, isLoading: locationsLoading } =
    useGetLocationsQuery();
  const { data: serviceTypeData, isLoading: serviceTypeLoading } =
    useGetServiceTypeQuery();

  // API Mutations
  const [addServiceFee, { isLoading: isAddingService }] =
    useAddServiceFeeMutation();
  const [updateServiceFee, { isLoading: isUpdatingService }] =
    useUpdateServiceFeeMutation();
  const [deleteServiceFee, { isLoading: isDeletingService }] =
    useDeleteServiceFeeMutation();
  const [addVatFee, { isLoading: isAddingVat }] = useAddVatFeeMutation();
  const [updateVatFee, { isLoading: isUpdatingVat }] =
    useUpdateVatFeeMutation();
  const [deleteVatFee, { isLoading: isDeletingVat }] =
    useDeleteVatFeeMutation();
  const [addDeliveryFee, { isLoading: isAddingDelivery }] =
    useAddDeliveryFeeMutation();
  const [updateDeliveryFee, { isLoading: isUpdatingDelivery }] =
    useUpdateDeliveryFeeMutation();
  const [deleteDeliveryFee, { isLoading: isDeletingDelivery }] =
    useDeleteDeliveryFeeMutation();

  // State
  const [fees, setFees] = useState([]);
  const [vatFees, setVatFees] = useState([]);
  const [deliveryFees, setDeliveryFees] = useState([]);
  const [selectedFees, setSelectedFees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [newFee, setNewFee] = useState({
    location_ids: [], // changed from location_id: null
    service_type_id: null,
    service_fee: 0,
    vat_fee: 0,
    name: "",
    fee: 0,
  });
  const [feeType, setFeeType] = useState("service");

  // Delete modal state
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [feeToDelete, setFeeToDelete] = useState(null);
  const [feeTypeToDelete, setFeeTypeToDelete] = useState("");

  // Process Service Fees
  useEffect(() => {
    if (serviceFeesData?.length) {
      setFees(
        serviceFeesData.map((fee, index) => ({
          key: fee.id?.toString() || (index + 1).toString(),
          id: fee.id,
          location: fee.location,
          service_type: fee.service_type,
          state: fee.location?.state?.name || "",
          service_fee: fee.service_fee || 0,
        })),
      );
    }
  }, [serviceFeesData]);

  // Process VAT Fees
  useEffect(() => {
    const vatList = vatFeesData?.data || vatFeesData;
    if (Array.isArray(vatList) && vatList.length) {
      setVatFees(
        vatList.map((fee, index) => ({
          key: fee.id?.toString() || (index + 1).toString(),
          id: fee.id,
          location: fee.location,
          state: fee.location?.state?.name || "",
          vat_fee: parseFloat(fee.vat_fee || 0),
        })),
      );
    }
  }, [vatFeesData]);

  // Process Delivery Fees
  useEffect(() => {
    const deliveryList = deliveryFeesData?.data || deliveryFeesData;
    if (Array.isArray(deliveryList) && deliveryList.length) {
      setDeliveryFees(
        deliveryList.map((fee, index) => ({
          key: fee.id?.toString() || (index + 1).toString(),
          id: fee.id,
          location: fee.location,
          name: fee.name || "Standard",
          fee: parseFloat(fee.fee || 0),
        })),
      );
    }
  }, [deliveryFeesData]);

  // Handle errors
  useEffect(() => {
    if (serviceError) {
      message.error(
        serviceError?.data?.message || "Failed to fetch service fees",
      );
    }
    if (vatError) {
      message.error(vatError?.data?.message || "Failed to fetch VAT fees");
    }
    if (deliveryError) {
      message.error(
        deliveryError?.data?.message || "Failed to fetch delivery fees",
      );
    }
  }, [serviceError, vatError, deliveryError]);

  // Prepare select options
  const locationOptions =
    locationsData?.map((l) => ({
      value: l.id,
      label: l.name,
    })) || [];

  const serviceTypeOptions =
    serviceTypeData?.map((st) => ({
      value: st.id,
      label: st.name,
    })) || [];

  // Table Columns
  const feeColumns = [
    { title: "Location", dataIndex: ["location", "name"], key: "location" },
    { title: "State", dataIndex: "state", key: "state" },
    {
      title: "Service Type",
      dataIndex: ["service_type", "name"],
      key: "service_type",
    },
    {
      title: "Service Fee (%)",
      dataIndex: "service_fee",
      key: "service_fee",
      render: (value) => `${value}%`,
    },
  ];

  const vatColumns = [
    { title: "Location", dataIndex: ["location", "name"], key: "location" },
    { title: "State", dataIndex: "state", key: "state" },
    {
      title: "VAT Fee (%)",
      dataIndex: "vat_fee",
      key: "vat_fee",
      render: (value) => `${value}%`,
    },
  ];

  const deliveryColumns = [
    { title: "Location", dataIndex: ["location", "name"], key: "location" },
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Fee (₦/km)",
      dataIndex: "fee",
      key: "fee",
      render: (value) => `₦${value.toLocaleString()}`,
    },
  ];

  // Validation helper
  const validateFee = () => {
    if (!newFee.location_ids || newFee.location_ids.length === 0) {
      message.warning("Please select at least one location");
      return false;
    }

    if (feeType === "service") {
      if (!newFee.service_type_id) {
        message.warning("Please select a service type");
        return false;
      }
      if (
        !newFee.service_fee ||
        newFee.service_fee < 1 ||
        newFee.service_fee > 100
      ) {
        message.warning("Service fee must be between 1% and 100%");
        return false;
      }
    }

    if (feeType === "vat") {
      if (!newFee.vat_fee || newFee.vat_fee < 1 || newFee.vat_fee > 100) {
        message.warning("VAT fee must be between 1% and 100%");
        return false;
      }
    }

    if (feeType === "delivery") {
      if (newFee.fee === undefined || newFee.fee < 0) {
        message.warning("Please enter a valid delivery fee");
        return false;
      }
    }

    return true;
  };

  // Add Fee Handler - fires one API call per selected location in parallel
  const handleAddFee = async () => {
    if (!validateFee()) return;

    try {
      const promises = newFee.location_ids.map((locationId) => {
        if (feeType === "service") {
          return addServiceFee({
            location_id: locationId,
            service_type_id: newFee.service_type_id,
            service_fee: newFee.service_fee,
          }).unwrap();
        } else if (feeType === "vat") {
          return addVatFee({
            location_id: locationId,
            vat_fee: newFee.vat_fee,
          }).unwrap();
        } else if (feeType === "delivery") {
          const payload = {
            location_id: locationId,
            fee: newFee.fee,
          };
          if (newFee.name?.trim()) payload.name = newFee.name.trim();
          return addDeliveryFee(payload).unwrap();
        }
        return Promise.resolve();
      });

      await Promise.all(promises);

      const label = feeType.charAt(0).toUpperCase() + feeType.slice(1);
      message.success(
        `${label} fee${newFee.location_ids.length > 1 ? "s" : ""} added successfully!`,
      );

      if (feeType === "service") refetchService();
      else if (feeType === "vat") refetchVat();
      else if (feeType === "delivery") refetchDelivery();

      closeModal();
    } catch (err) {
      message.error(err?.data?.message || "Failed to add fee");
    }
  };

  // Update Fee Handler - edit mode always has a single location (location_ids[0])
  const handleUpdateFee = async () => {
    if (!validateFee()) return;

    try {
      const locationId = newFee.location_ids[0];

      if (feeType === "service") {
        await updateServiceFee({
          id: editingFee.id,
          requestData: {
            location_id: locationId,
            service_type_id: newFee.service_type_id,
            service_fee: newFee.service_fee,
          },
        }).unwrap();
        message.success("Service fee updated successfully!");
        refetchService();
      } else if (feeType === "vat") {
        await updateVatFee({
          id: editingFee.id,
          requestData: {
            location_id: locationId,
            vat_fee: newFee.vat_fee,
          },
        }).unwrap();
        message.success("VAT fee updated successfully!");
        refetchVat();
      } else if (feeType === "delivery") {
        const payload = {
          location_id: locationId,
          fee: newFee.fee,
        };
        if (newFee.name?.trim()) payload.name = newFee.name.trim();

        await updateDeliveryFee({
          id: editingFee.id,
          requestData: payload,
        }).unwrap();
        message.success("Delivery fee updated successfully!");
        refetchDelivery();
      }

      closeModal();
    } catch (err) {
      message.error(err?.data?.message || "Failed to update fee");
    }
  };

  // Edit Fee Handler - wraps single location id in array for unified state shape
  const handleEditFee = (record) => {
    setEditingFee(record);

    if (record.service_fee !== undefined) {
      setFeeType("service");
      setNewFee({
        location_ids: record.location?.id ? [record.location.id] : [],
        service_type_id: record.service_type?.id,
        service_fee: record.service_fee,
        vat_fee: 0,
        name: "",
        fee: 0,
      });
    } else if (record.vat_fee !== undefined) {
      setFeeType("vat");
      setNewFee({
        location_ids: record.location?.id ? [record.location.id] : [],
        service_type_id: null,
        service_fee: 0,
        vat_fee: record.vat_fee,
        name: "",
        fee: 0,
      });
    } else if (record.fee !== undefined) {
      setFeeType("delivery");
      setNewFee({
        location_ids: record.location?.id ? [record.location.id] : [],
        service_type_id: null,
        service_fee: 0,
        vat_fee: 0,
        name: record.name || "",
        fee: record.fee,
      });
    }

    setShowModal(true);
  };

  // Delete Modal Handlers
  const handleOpenDeleteModal = (record, type) => {
    setFeeToDelete(record);
    setFeeTypeToDelete(type);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!feeToDelete) return;

    try {
      if (feeTypeToDelete === "service") {
        await deleteServiceFee(feeToDelete.id).unwrap();
        message.success("Service fee deleted successfully!");
        refetchService();
      } else if (feeTypeToDelete === "vat") {
        await deleteVatFee(feeToDelete.id).unwrap();
        message.success("VAT fee deleted successfully!");
        refetchVat();
      } else if (feeTypeToDelete === "delivery") {
        await deleteDeliveryFee(feeToDelete.id).unwrap();
        message.success("Delivery fee deleted successfully!");
        refetchDelivery();
      }
      closeDeleteModal();
    } catch (err) {
      message.error(err?.data?.message || "Failed to delete fee");
    }
  };

  // Helper functions
  const closeModal = () => {
    setShowModal(false);
    setEditingFee(null);
    setNewFee({
      location_ids: [],
      service_type_id: null,
      service_fee: 0,
      vat_fee: 0,
      name: "",
      fee: 0,
    });
  };

  const closeDeleteModal = () => {
    setIsDeleteModalVisible(false);
    setFeeToDelete(null);
    setFeeTypeToDelete("");
  };

  const handleSelectFees = (keys) => setSelectedFees(keys);

  const isModalLoading =
    isAddingService ||
    isAddingVat ||
    isAddingDelivery ||
    isUpdatingVat ||
    isUpdatingService ||
    isUpdatingDelivery ||
    locationsLoading ||
    serviceTypeLoading;

  return (
    <div className="h-full bg-white">
      {/* Delivery Fee Section */}
      <FeeSection
        title="Delivery Fees"
        description="Manage delivery fees per kilometer for different locations."
        data={deliveryFees}
        columns={deliveryColumns}
        onEdit={handleEditFee}
        onDelete={(record) => handleOpenDeleteModal(record, "delivery")}
        onSelect={handleSelectFees}
        selectedKeys={selectedFees}
        loading={isLoadingDelivery}
        onShowAdd={() => {
          setFeeType("delivery");
          setShowModal(true);
        }}
      />

      {/* Service Fee Section */}
      <FeeSection
        title="Service Fees"
        description="Manage service fees for different locations and service types."
        data={fees}
        columns={feeColumns}
        onEdit={handleEditFee}
        onDelete={(record) => handleOpenDeleteModal(record, "service")}
        onSelect={handleSelectFees}
        selectedKeys={selectedFees}
        loading={isLoadingService}
        onShowAdd={() => {
          setFeeType("service");
          setShowModal(true);
        }}
      />

      {/* VAT Fee Section */}
      <FeeSection
        title="VAT Fees"
        description="Manage VAT fees for different locations."
        data={vatFees}
        columns={vatColumns}
        onEdit={handleEditFee}
        onDelete={(record) => handleOpenDeleteModal(record, "vat")}
        onSelect={handleSelectFees}
        selectedKeys={selectedFees}
        loading={isLoadingVat}
        onShowAdd={() => {
          setFeeType("vat");
          setShowModal(true);
        }}
      />

      {/* Fee Modal */}
      <FeeModal
        isOpen={showModal}
        onClose={closeModal}
        title={
          editingFee
            ? `Edit ${feeType.charAt(0).toUpperCase() + feeType.slice(1)} Fee`
            : `Add ${feeType.charAt(0).toUpperCase() + feeType.slice(1)} Fee`
        }
        subtitle={
          editingFee ? "Update fee information" : "Enter fee information"
        }
        loading={isModalLoading}
        fee={newFee}
        setFee={setNewFee}
        onSubmit={editingFee ? handleUpdateFee : handleAddFee}
        locationOptions={locationOptions}
        serviceTypeOptions={serviceTypeOptions}
        feeType={feeType}
        mode={editingFee ? "edit" : "add"}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        title={`Delete ${feeTypeToDelete.charAt(0).toUpperCase() + feeTypeToDelete.slice(1)} Fee`}
        isOpen={isDeleteModalVisible}
        handleClose={closeDeleteModal}
        onDelete={handleConfirmDelete}
        content={`Are you sure you want to delete this ${feeTypeToDelete} fee for ${feeToDelete?.location?.name || "this location"}?`}
        loading={isDeletingService || isDeletingVat || isDeletingDelivery}
      />
    </div>
  );
};

export default Fees;
